import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ExternalLink, Home } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const VegrAboutPage = () => {
  const otherApps = [
    { name: 'Mímir', description: 'Dreams and aspirations', theme: 'Memory and wisdom' },
    { name: 'Saga', description: 'Data narratives', theme: 'Stories and records' },
    { name: 'Eir', description: 'Medical pluralism', theme: 'Healing and health' },
    { name: 'Yggdrasil', description: 'Archives', theme: 'Knowledge tree' },
    { name: 'Skald', description: 'Learning', theme: 'Education and poetry' },
    { name: 'Vegr', description: 'Roads and journeys', theme: 'Current app', current: true }
  ];

  return (
    <div className="min-h-screen bg-[#E8DED0]">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#2C4A5E] to-[#1e3544] text-white py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center text-center">
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_uoe-highland/artifacts/pk36mmt7_Vegr%20roads%20app%20transparent.png" 
                alt="Vegr Logo" 
                className="w-32 h-32"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </div>
            <h1 className="text-5xl font-bold mb-4">About Vegr</h1>
            <p className="text-xl text-white/90 max-w-2xl">
              Real-time road and weather monitoring for Northeast India
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <Card className="shadow-xl mb-8">
            <CardContent className="p-8">
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  <strong className="text-[#2C4A5E]">Vegr</strong> (from the Old Norse <em>vegr</em>, meaning road or way) is developed by the <strong>Digital Laboratory</strong> at the <strong>Highland Institute</strong>. The app provides real-time information on road and weather conditions across the mountainous regions of Northeast India and Highland Asia, where landslides, monsoon rains, and changing climate patterns affect connectivity and mobility.
                </p>
                
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Drawing from open meteorological, satellite, and transport datasets, Vegr helps users navigate highland routes safely and efficiently. It is part of the Highland Institute's broader effort to create digital tools that combine research, technology, and local knowledge to strengthen community resilience and climate adaptation.
                </p>

                <div className="bg-[#2C4A5E]/5 border-l-4 border-[#2C4A5E] p-6 rounded-r-lg my-8">
                  <h3 className="text-xl font-bold text-[#2C4A5E] mb-3">Key Features</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-[#4A7C59] mr-2 mt-1 flex-shrink-0" />
                      <span><strong>Crowdsourced Reports:</strong> Real-time road condition updates from local users</span>
                    </li>
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-[#4A7C59] mr-2 mt-1 flex-shrink-0" />
                      <span><strong>Weather Integration:</strong> Live weather data from 19 stations across Northeast India</span>
                    </li>
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-[#4A7C59] mr-2 mt-1 flex-shrink-0" />
                      <span><strong>Monsoon Alerts:</strong> Enhanced warnings during monsoon season (June-September)</span>
                    </li>
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-[#4A7C59] mr-2 mt-1 flex-shrink-0" />
                      <span><strong>Interactive Map:</strong> Visualize road conditions across 8 states</span>
                    </li>
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-[#4A7C59] mr-2 mt-1 flex-shrink-0" />
                      <span><strong>SMS Integration:</strong> Report conditions via SMS (coming soon)</span>
                    </li>
                  </ul>
                </div>

                <p className="text-lg text-gray-700 leading-relaxed">
                  Vegr belongs to a suite of apps designed by the Highland Institute, including <strong>Mímir</strong> (dreams), <strong>Saga</strong> (data), <strong>Eir</strong> (medical pluralism), <strong>Yggdrasil</strong> (archives), and <strong>Skald</strong> (learning).
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Highland Institute Apps Suite */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Highland Institute App Suite</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {otherApps.map((app) => (
                <Card 
                  key={app.name}
                  className={`hover:shadow-lg transition-all ${
                    app.current ? 'ring-2 ring-[#4A7C59] bg-[#4A7C59]/5' : ''
                  }`}
                >
                  <CardContent className="p-4 text-center">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{app.name}</h3>
                    <p className="text-xs text-gray-600 mb-1">{app.description}</p>
                    <p className="text-xs text-[#2C4A5E] italic">{app.theme}</p>
                    {app.current && (
                      <div className="mt-2">
                        <span className="text-xs bg-[#4A7C59] text-white px-2 py-1 rounded-full">
                          Current App
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Digital Laboratory */}
          <Card className="shadow-xl mb-8 bg-gradient-to-br from-[#2C4A5E] to-[#1e3544] text-white">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-4">Digital Laboratory</h2>
              <p className="text-white/90 leading-relaxed mb-4">
                The Digital Laboratory at the Highland Institute develops technology solutions that integrate 
                academic research, indigenous knowledge, and community needs. Our apps address challenges in 
                climate adaptation, healthcare, education, and cultural preservation across Highland Asia.
              </p>
              <a 
                href="https://highlandinstitute.org" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center text-white hover:text-green-400 transition-colors"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Visit Highland Institute
              </a>
            </CardContent>
          </Card>

          {/* Copyright */}
          <div className="text-center py-8 border-t border-gray-200">
            <p className="text-gray-700 mb-2">
              © 2025 The Highland Institute
            </p>
            <p className="text-gray-600 text-sm mb-4">
              Digital Laboratory | 
              <a 
                href="https://www.highlandinstitute.org" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#2C4A5E] hover:text-[#4A7C59] ml-1"
              >
                www.highlandinstitute.org
              </a>
            </p>
            <p className="text-gray-500 text-sm">All rights reserved.</p>
          </div>

          {/* Back to Map */}
          <div className="text-center">
            <Link to="/vegr">
              <Button className="bg-[#4A7C59] hover:bg-[#3d6949] text-white px-8 py-6 text-lg">
                <Home className="w-5 h-5 mr-2" />
                Back to Map
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VegrAboutPage;